import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { UpdatePaymentInfoDtoOut } from '@/models/out/UpdatePaymentInfoDtoOut';
import { PaymentMethodsDtoIn } from '@/models/in/PaymentMethodsDtoIn';
import { usePutAuthApi, useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { ReturnedPaymentImpactDtoIn } from '@/models/in/ReturnedPaymentDtoIn';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import styles from './ApprovedAmountDialog.module.css';

interface ChangeBankAccountDialogProps {
  open: boolean;
  onClose: () => void;
  request: ManagementSupportRequestDtoIn;
  onSuccess: () => void;
}

const ChangeBankAccountDialog = ({
  open,
  onClose,
  request,
  onSuccess,
}: ChangeBankAccountDialogProps) => {
  const {
    multiRequestCorrelationId: correlationId,
    multiRequestCounter: counter,
    isInInitialStatusProcess,
    paymentInfoId: currentPaymentInfoId,
    beneficiaryContact,
  } = request;
  const { t } = useTranslation();
  const [selectedPaymentInfoId, setSelectedPaymentInfoId] = useState<number | null>(currentPaymentInfoId);
  const [error, setError] = useState<string | null>(null);

  // Fetch bank accounts for the beneficiary contact
  const { data: paymentMethods, isLoading: isLoadingAccounts } = useGetAuthApi<PaymentMethodsDtoIn>(
    open && beneficiaryContact ? `management/support-requests/contacts/${beneficiaryContact.id}/payment-methods` : ''
  );

  const { mutate: updatePaymentInfo, isPending: isUpdating } = usePutAuthApi<void, object, UpdatePaymentInfoDtoOut>(
    'management/support-requests/payment-info'
  );

  /*
   * HAT-1395: an account deleted because a payment to it bounced is wrong for every request that
   * pays to it, not only this month. Replacing it here month by month left the supported's other
   * requests waiting for the coordinator, and this month still «תשלום חזר». So it is replaced the way
   * the coordinator would replace it - in every live request of the fund - and the months that
   * bounced are decided the same way: approved again if the fund's balance covers them, in
   * «ממתין לאישור» if not.
   * And if the account was fine after all, the office brings it back from here.
   */
  const isReturnedAccount = !!request.paymentInfoDeletedForReturnedPayment && currentPaymentInfoId != null;
  const { data: impact } = useGetAuthApi<ReturnedPaymentImpactDtoIn>(
    open && isReturnedAccount && counter != null ? `management/support-requests/${correlationId}/${counter}/returned-payment-impact` : ''
  );
  const { mutate: replaceReturnedAccount, isPending: isReplacing } = usePutAuthApi<void, object, { paymentInfoId: number }>(
    `management/support-requests/returned-payments/${request.fundId}/${currentPaymentInfoId}`
  );
  const { mutate: restoreAccount, isPending: isRestoring } = usePostAuthApi<{ requests: number }, object>(
    `management/support-requests/returned-payments/${currentPaymentInfoId}/restore`
  );
  const isSubmitting = isUpdating || isReplacing || isRestoring;

  const accounts = paymentMethods?.accounts ?? [];
  const isCurrentAccountMissing = accounts.length > 0 && !accounts.some(a => a.id === currentPaymentInfoId);

  // Initialize selection when dialog opens; reset to null if the linked account no longer exists
  useEffect(() => {
    if (open) {
      setSelectedPaymentInfoId(isCurrentAccountMissing ? null : currentPaymentInfoId);
      setError(null);
    }
  }, [open, currentPaymentInfoId, isCurrentAccountMissing]);

  const handleSubmit = () => {
    // Check if payment info actually changed
    if (selectedPaymentInfoId === currentPaymentInfoId) {
      handleClose();
      return;
    }

    if (!selectedPaymentInfoId) {
      setError(t('pleaseSelectBankAccount'));
      return;
    }

    setError(null);

    if (isReturnedAccount) {
      replaceReturnedAccount(
        { body: { paymentInfoId: selectedPaymentInfoId } },
        {
          onSuccess: () => {
            onSuccess();
            handleClose();
          },
          onError: (err) => {
            setError(t('errorUpdatingBankAccount') + ': ' + err.message);
          },
        }
      );
      return;
    }

    const body: UpdatePaymentInfoDtoOut = {
      correlationId,
      counter: isInInitialStatusProcess ? null : (counter ?? null),
      isInInitialStatusProcess,
      paymentInfoId: selectedPaymentInfoId!,
    };

    updatePaymentInfo(
      { body },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: (err) => {
          setError(t('errorUpdatingBankAccount') + ': ' + err.message);
        },
      }
    );
  };

  const handleRestore = () => {
    if (isSubmitting) return;
    setError(null);
    restoreAccount(
      {},
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: () => setError(t('returnedPayment.officeRestoreFailed')),
      }
    );
  };

  const handleClose = () => {
    setSelectedPaymentInfoId(currentPaymentInfoId);
    setError(null);
    onClose();
  };

  const handleSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPaymentInfoId(Number(e.target.value));
    // Clear error when user changes selection
    if (error) {
      setError(null);
    }
  };

  if (!beneficiaryContact || currentPaymentInfoId == null) {
    return (
      <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) handleClose(); }} title={t('changeBankAccount')}>
        <div className={styles.content}>
          <p>{t('cannotChangeBankAccount')}</p>
          <Button variant="white" onClick={handleClose}>{t('close')}</Button>
        </div>
      </MainDialogLayout>
    );
  }

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) {
          handleClose();
        }
      }}
      title={t('changeBankAccount')}
    >
      <div className={styles.content}>
        <p className={styles.subtitle}>
          {t('supportedName')}: {beneficiaryContact.firstName} {beneficiaryContact.lastName}
        </p>

        {isReturnedAccount ? (
          <Alert
            type="warning"
            dismissible={false}
            body={t('returnedPayment.officeChangeAccountNotice', { count: (impact?.otherRequestsInFund ?? 0) + 1 })}
            action={{ label: t('returnedPayment.officeRestoreAccount'), onClick: handleRestore }}
          />
        ) : (
          isCurrentAccountMissing && <p className={styles.warning}>{t('currentBankAccountDeleted')}</p>
        )}

        <div className={styles.inputSection}>
          <label htmlFor="bankAccount" className={styles.inputLabel}>
            {t('selectBankAccount')}:
          </label>
          {isLoadingAccounts ? (
            <p>{t('loading')}...</p>
          ) : accounts.length === 0 ? (
            <p>{t('noBankAccountsFound')}</p>
          ) : (
            <select
              id="bankAccount"
              className={`${styles.amountInput} ${error ? styles.hasError : ''}`}
              value={selectedPaymentInfoId ?? ""}
              onChange={handleSelectionChange}
            >
              <option value="" disabled>{t('selectBankAccount')}</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {t('bank')} {account.bankNumber} ({account.bankName}), {t('branch')} {account.branchNumber}, {t('account')} {account.accountNumber}
                </option>
              ))}
            </select>
          )}
        </div>

        {error && (
          <p className={styles.error}>{error}</p>
        )}

        <div className={styles.actions}>
          <Button
            variant="white"
            className={styles.secondaryButton}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('close')}
          </Button>
          <Button
            variant="primary"
            className={styles.actionButton}
            onClick={handleSubmit}
            disabled={isSubmitting || isLoadingAccounts || accounts.length === 0}
          >
            {isSubmitting ? t('loading') : t('save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default ChangeBankAccountDialog;

